import React, { useState } from "react";
import { Grid, Typography, Stack, Button, Avatar, Skeleton, Box } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import CommentSection from "../CommentSection";
import Styles from "../TaskDetails.module.scss";

const Activity = ({
  selectedData,
  currentUser,
  GetSeparateTaskId,
  fromClient,
  onAddComment,
  loadingHistory,
  loadingComments,
  loadMoreHistory,
  loadMoreComments,
  hasMoreHistory,
  hasMoreComments,
}) => {
  const [showComments, setShowComments] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  const handleHistoryClick = () => {
    setShowHistory(true);
    setShowComments(false);
  };

  const handleCommentsClick = () => {
    setShowComments(true);
    setShowHistory(false);
  };

  const validationSchema = Yup.object().shape({
    comments: Yup.string()
      .test("is-not-empty", "Comment cannot be empty", (value) => {
        const trimmed = value?.replace(/<(.|\n)*?>/g, "").trim();
        return Boolean(trimmed);
      })
      .required("Comment is required"),
  });

  const formatChanges = (text) => {
    if (!text) return text;

    // Check if this is a reopen entry and highlight it in red
    if (text.includes("Task reopened for revision") && text.includes("Reopen #")) {
      return `<span style="color: #D50707; font-weight: 600;">${text}</span>`;
    }

    // Check if this is a bug creation entry and highlight "Bug" in red
    if (text.includes("was created for this task")) {
      return text.replace(
        /Bug "([^"]+)"/g,
        '<span style="color: #D50707; font-weight: 700;">Bug "$1"</span>'
      );
    }

    return text
      .replace(
        /(Task Type|Assignee|from|to|BUG|TESTING|FEATURE|Status|Task|for|removed|added)/gi,
        "<strong>$&</strong>"
      )
      .replace(/\b([A-Z][a-z]+ [A-Z][a-z]+)\b/g, "<strong>$&</strong>");
  };

  return (
    <>
      <Typography
        sx={{
          fontSize: "16px",
          color: "#181818",
          mt: 3,
          mb: 1.5,
          fontWeight: 700,
        }}
      >
        Activity
      </Typography>
      <Stack direction={"row"} spacing={1} alignItems={"center"}>
        <Typography
          sx={{ fontSize: "16px", fontWeight: 400, color: "#737171" }}
        >
          Show :
        </Typography>
        <Stack
          sx={{
            color: showComments ? "#055253" : "#4d4c4c",
            background: showComments ? "#e7f5f6" : "#f3f4f6",
            padding: "0 5px",
            fontWeight: 600,
            fontSize: "14px",
            alignItems: "center",
            cursor: "pointer",
            borderRadius: "4px",
          }}
          onClick={handleCommentsClick}
        >
          Comments
        </Stack>
        {!fromClient && (
          <Stack
            onClick={handleHistoryClick}
            sx={{
              color: showHistory ? "#055253" : "#4d4c4c",
              background: showHistory ? "#e7f5f6" : "#f3f4f6",
              padding: "0 5px",
              fontWeight: 600,
              fontSize: "14px",
              alignItems: "center",
              cursor: "pointer",
              borderRadius: "4px",
            }}
          >
            History
          </Stack>
        )}
      </Stack>

      {showComments && (
        <>
          {/* Comment Input Form */}
          <Formik
            initialValues={{ comments: "" }}
            validationSchema={validationSchema}
            onSubmit={(values, { resetForm }) => {
              onAddComment(values.comments);
              resetForm();
            }}
          >
            {({
              values,
              resetForm,
              touched,
              errors,
              setFieldValue,
              validateForm,
              validateField,
              setTouched,
            }) => (
              <Form>
                <Stack>
                  <Grid item xs={12} sm={12} mt={1}>
                    <ReactQuill
                      value={values.comments || ""}
                      onChange={(value) => {
                        setFieldValue("comments", value);
                      }}
                      placeholder="Enter Comments"
                      className="EditTextInput"
                      style={{
                        borderRadius: "8px",
                        height: "100px",
                        marginBottom: "40px",
                      }}
                      modules={{
                        toolbar: [
                          [{ header: "1" }, { header: "2" }, { font: [] }],
                          [{ list: "ordered" }, { list: "bullet" }],
                          ["bold", "italic", "underline"],
                          [{ align: [] }],
                        ],
                      }}
                    />
                    {touched.comments && errors.comments && (
                      <div
                        className="form-error"
                        style={{ color: "red", fontSize: "12px" }}
                      >
                        {errors.comments}
                      </div>
                    )}
                  </Grid>
                  <Stack direction={"row"} mt={3.5} mb={2} gap={2}>
                    <Button
                      onClick={() => {
                        setFieldValue("comments", "");
                      }}
                      className={Styles.commentCancelBtn}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className={Styles.commentSaveBtn}>
                      Save
                    </Button>
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>

          {/* Comments List */}
          <Grid ml={1}>
            {loadingComments && (!selectedData?.comments || selectedData.comments.length === 0) ? (
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
              </Stack>
            ) : (
              <>
                <CommentSection
                  selectedData={selectedData}
                  currentUser={currentUser}
                  GetSeparateTaskId={GetSeparateTaskId}
                />
                {hasMoreComments && (
                  <Box display="flex" justifyContent="center" mt={2} mb={2}>
                    <Button
                      onClick={loadMoreComments}
                      disabled={loadingComments}
                      variant="outlined"
                      size="small"
                      sx={{
                        textTransform: "none",
                        color: "#055253",
                        borderColor: "#055253",
                        "&:hover": {
                          borderColor: "#055253",
                          backgroundColor: "#e7f5f6",
                        },
                      }}
                    >
                      {loadingComments ? "Loading..." : "Load More Comments"}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Grid>
        </>
      )}
      {showHistory && !fromClient && (
        <Grid ml={1}>
          {loadingHistory && (!selectedData?.history || selectedData.history.length === 0) ? (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {[1, 2, 3].map((i) => (
                <Stack key={i} direction="row" spacing={2} alignItems="center">
                  <Skeleton variant="circular" width={40} height={40} />
                  <Stack flex={1}>
                    <Skeleton variant="text" width="40%" />
                    <Skeleton variant="text" width="80%" />
                  </Stack>
                </Stack>
              ))}
            </Stack>
          ) : (
            <>
              {selectedData?.history?.map((task, index) => {
                const taskDateString = task?.date;
                if (!taskDateString || isNaN(new Date(taskDateString).getTime())) {
                  return null;
                }

                const taskDate = new Date(taskDateString);
                const formattedDate = new Intl.DateTimeFormat("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                  timeZone: "UTC",
                }).format(taskDate);

                return (
                  <div
                    key={index}
                    className="no-scrollbar"
                    style={{ overflow: "auto", marginTop: "10px" }}
                  >
                    <div style={{ marginBottom: "10px" }}>
                      <Stack direction={"row"} alignItems={"center"} spacing={1}>
                        <Stack>
                          <Avatar
                            alt={task?.assigned?.name}
                            src={task?.assigned?.file}
                            style={{
                              backgroundColor: "#C7EEEF",
                              color: "#055253",
                              height: "40px",
                              width: "40px",
                              fontSize: "14px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {!task?.assigned?.file &&
                              task?.assigned?.name?.charAt(0)?.toUpperCase()}
                          </Avatar>
                        </Stack>
                        <Stack spacing={0.5} style={{ width: "100%" }}>
                          <Stack direction={"row"} alignItems={"center"}>
                            <span
                              style={{
                                fontWeight: "bold",
                                paddingRight: "5px",
                              }}
                            >
                              {task?.assigned?.name}
                            </span>
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#4D4C4C",
                                marginLeft: "10px",
                              }}
                            >
                              {`${formattedDate.toString()}`}
                            </span>
                          </Stack>
                          <Stack>
                            <div
                              style={{
                                color: "#000000",
                                fontSize: "14px",
                                lineHeight: "1.4",
                              }}
                              dangerouslySetInnerHTML={{
                                __html: formatChanges(task.changes),
                              }}
                            />
                          </Stack>
                        </Stack>
                      </Stack>
                    </div>
                  </div>
                );
              })}
              {hasMoreHistory && (
                <Box display="flex" justifyContent="center" mt={2} mb={2}>
                  <Button
                    onClick={loadMoreHistory}
                    disabled={loadingHistory}
                    variant="outlined"
                    size="small"
                    sx={{
                      textTransform: "none",
                      color: "#055253",
                      borderColor: "#055253",
                      "&:hover": {
                        borderColor: "#055253",
                        backgroundColor: "#e7f5f6",
                      },
                    }}
                  >
                    {loadingHistory ? "Loading..." : "Load More Activity"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Grid>
      )}
    </>
  );
};

export default Activity;
